import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';

// wavesurfer.js needs concrete color strings, not CSS variables, so resolve the
// design tokens against the live document (re-reads on each mount so a runtime
// light/dark theme switch is picked up the next time a track loads).
const resolveToken = (name, fallback) => {
    if (typeof window === 'undefined') return fallback;
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
};

/**
 * @param {object}   props
 * @param {string}   props.src                streaming audio source (signed URL / object URL)
 * @param {number[]} [props.peaks]            precomputed waveform peaks ([min,max]…) — when
 *                                            supplied with `peaksDuration`, the waveform is drawn
 *                                            from these and the browser streams playback via a
 *                                            MediaElement instead of downloading + decoding the
 *                                            whole file (see audioPeaksService / SegmentEditor).
 * @param {number}   [props.peaksDuration]    media length in seconds that pairs with `peaks`
 * @param {boolean}  [props.waitingForPeaks]  host is still fetching peaks for a large file — hold
 *                                            off mounting WaveSurfer so we never kick off the
 *                                            full-file decode fallback in the meantime
 */
const WaveformPlayer = ({ src, className, peaks, peaksDuration = 0, waitingForPeaks = false }) => {
    const containerRef = useRef(null);
    const wavesurferRef = useRef(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(0.8);
    const [speed, setSpeed] = useState(1);
    const [preservePitch, setPreservePitch] = useState(true);
    const [isLooping, setIsLooping] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState(false);

    // BUG-5: keep live values in refs so the WaveSurfer event handlers (created
    // once per `src`) don't close over stale state (the loop toggle, volume,
    // speed and pitch lock all broke after the initial render otherwise).
    const isLoopingRef = useRef(isLooping);
    const volumeRef = useRef(volume);
    const speedRef = useRef(speed);
    const preservePitchRef = useRef(preservePitch);
    useEffect(() => { isLoopingRef.current = isLooping; }, [isLooping]);
    useEffect(() => { volumeRef.current = volume; }, [volume]);
    useEffect(() => { speedRef.current = speed; }, [speed]);
    useEffect(() => { preservePitchRef.current = preservePitch; }, [preservePitch]);

    // Format time in mm:ss
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        // Reset transient state whenever the source changes so stale time/
        // duration/loaded flags from the previous track don't linger. Seed the
        // duration from the precomputed peaks (when present) so the total time
        // shows immediately, before 'ready' fires.
        setIsLoaded(false);
        setLoadError(false);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(peaksDuration > 0 ? peaksDuration : 0);

        // The host is still fetching peaks for a large file. Hold off creating
        // WaveSurfer — mounting now with only `src` would trigger the full-file
        // fetch+decode path we're trying to avoid. The placeholder is rendered
        // below; this effect re-runs once peaks arrive (waitingForPeaks → false).
        if (waitingForPeaks) return;

        const createOptions = {
            container: containerRef.current,
            waveColor: resolveToken('--qd-border-strong', '#CBD5E1'),     // unplayed
            progressColor: resolveToken('--qd-accent', '#3B82F6'),        // active
            cursorColor: resolveToken('--qd-accent-active', '#1D4ED8'),
            barWidth: 2,
            barGap: 2,
            barRadius: 2,
            height: 72,               // Slightly taller for better visibility
            normalize: true,
            cursorWidth: 2,
            interact: true,
            url: src,
        };
        // Large-file fast path (mirrors SegmentEditor): when the backend has
        // precomputed peaks, draw the waveform from them and let the MediaElement
        // stream playback via range requests — the whole file is never fetched or
        // decoded in the browser. Without peaks, fall back to WaveSurfer's default
        // decode-the-whole-file behaviour (fine for small/local files).
        if (peaks && peaks.length > 0 && peaksDuration > 0) {
            createOptions.peaks = [peaks];
            createOptions.duration = peaksDuration;
        }

        const ws = WaveSurfer.create(createOptions);

        // BUG-4: assign the ref immediately, NOT inside the 'ready' handler.
        // If 'ready' never fires (decode error / AbortError on fast switch) the
        // ref used to stay null and every control silently no-op'd. Load failures
        // surface via the 'error' handler below (AbortError filtered there).
        wavesurferRef.current = ws;

        ws.on('ready', () => {
            ws.setVolume(volumeRef.current);
            ws.setPlaybackRate(speedRef.current, preservePitchRef.current);
            setDuration(ws.getDuration());
            setIsLoaded(true);
        });

        ws.on('error', (error) => {
            if (error?.name !== 'AbortError') {
                console.error('Wavesurfer error:', error);
                setLoadError(true);
            }
        });

        ws.on('audioprocess', () => setCurrentTime(ws.getCurrentTime()));
        // fe-media-5: wavesurfer v7 has no 'seek' event — that subscription never
        // fired, so the time readout froze when seeking while PAUSED. 'timeupdate'
        // fires on any current-time change (playback AND seeks).
        ws.on('timeupdate', () => setCurrentTime(ws.getCurrentTime()));

        ws.on('play', () => setIsPlaying(true));
        ws.on('pause', () => setIsPlaying(false));
        ws.on('finish', () => {
            if (isLoopingRef.current) {
                ws.play();
            } else {
                setIsPlaying(false);
            }
        });

        return () => {
            try {
                ws.destroy();
            } catch (error) {
                // Silently swallow teardown exceptions
            }
            wavesurferRef.current = null;
        };
    }, [src, peaks, peaksDuration, waitingForPeaks]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handlers
    const togglePlay = () => wavesurferRef.current?.playPause();

    const handleVolume = (e) => {
        const newVol = parseFloat(e.target.value);
        setVolume(newVol);
        wavesurferRef.current?.setVolume(newVol);
    };

    const handleSpeed = (e) => {
        const newSpeed = parseFloat(e.target.value);
        setSpeed(newSpeed);
        wavesurferRef.current?.setPlaybackRate(newSpeed, preservePitch);
    };

    const togglePitch = () => {
        const newPitchState = !preservePitch;
        setPreservePitch(newPitchState);
        wavesurferRef.current?.setPlaybackRate(speed, newPitchState);
    };

    return (
        <div
            className={`waveform-player ${className || ''}`}
            style={{
                background: 'var(--qd-surface)',
                border: '1px solid var(--qd-border)',
                borderRadius: 'var(--qd-radius-lg)',
                padding: 'var(--qd-space-5)',
                boxShadow: 'var(--qd-shadow-md)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--qd-space-4)',
                maxWidth: '100%',
                fontFamily: 'var(--qd-font-sans)'
            }}
        >
            {/* Top Bar: Time & Loop */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--qd-text-xs)', color: 'var(--qd-text-muted)', fontWeight: 'var(--qd-weight-medium)' }}>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                </span>

                <button
                    onClick={() => setIsLooping(!isLooping)}
                    title="Toggle Loop"
                    style={{
                        background: 'none',
                        border: 'none',
                        color: isLooping ? 'var(--qd-accent)' : 'var(--qd-text-subtle)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--qd-space-1)'
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="16 3 21 3 21 8"></polyline>
                        <line x1="4" y1="22" x2="21" y2="3"></line>
                        <path d="M21 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6"></path>
                    </svg>
                    Loop
                </button>
            </div>

            {/* While the host fetches peaks for a large file, show a lightweight
                placeholder at the waveform's height so the layout doesn't jump.
                The real container is mounted only once peaks are ready. */}
            {waitingForPeaks ? (
                <div style={{
                    width: '100%',
                    height: 72,
                    background: 'var(--qd-surface-2)',
                    borderRadius: 'var(--qd-radius-md)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--qd-text-subtle)',
                    fontSize: 'var(--qd-text-sm)',
                }}>
                    Preparing waveform…
                </div>
            ) : (
                /* Waveform Container */
                <div
                    ref={containerRef}
                    style={{
                        width: '100%',
                        background: 'var(--qd-surface-2)',
                        borderRadius: 'var(--qd-radius-md)',
                        padding: 'var(--qd-space-1)',
                        opacity: isLoaded ? 1 : 0.4,
                        transition: 'opacity var(--qd-transition-slow)',
                        cursor: isLoaded ? 'pointer' : 'default',
                        display: loadError ? 'none' : 'block'
                    }}
                />
            )}

            {/* BUG-4: surface load/decode failures instead of freezing silently */}
            {loadError && (
                <div style={{
                    width: '100%',
                    background: 'var(--qd-danger-subtle)',
                    border: '1px solid var(--qd-danger)',
                    color: 'var(--qd-danger)',
                    borderRadius: 'var(--qd-radius-md)',
                    padding: 'var(--qd-space-4)',
                    fontSize: 'var(--qd-text-sm)',
                    textAlign: 'center'
                }}>
                    Couldn’t load this audio file.
                </div>
            )}

            {/* Controls Row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--qd-space-5)', flexWrap: 'wrap', marginTop: 'var(--qd-space-1)' }}>

                {/* Play/Pause Button */}
                <button
                    onClick={togglePlay}
                    disabled={!isLoaded}
                    style={{
                        background: 'var(--qd-accent)',
                        color: 'var(--qd-text-on-accent)',
                        border: 'none',
                        borderRadius: 'var(--qd-radius-circle)',
                        width: 'var(--qd-control-md)',
                        height: 'var(--qd-control-md)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: isLoaded ? 'pointer' : 'not-allowed',
                        boxShadow: 'var(--qd-shadow-sm)',
                        transition: 'transform var(--qd-transition-fast), background var(--qd-transition)',
                        flexShrink: 0
                    }}
                    onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                    onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                    {isPlaying ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>
                    ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '2px' }}><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                    )}
                </button>

                <div style={{ width: '1px', height: '24px', background: 'var(--qd-border)' }}></div>

                {/* Volume Control */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--qd-space-2)', color: 'var(--qd-text-muted)', fontSize: 'var(--qd-text-sm)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                    <input
                        type="range"
                        min="0" max="1" step="0.05"
                        value={volume}
                        onChange={handleVolume}
                        style={{ width: '70px', accentColor: 'var(--qd-accent)', cursor: 'pointer' }}
                    />
                </div>

                {/* Speed Control */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--qd-space-2)', color: 'var(--qd-text-muted)', fontSize: 'var(--qd-text-sm)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    <span style={{ width: '32px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{speed.toFixed(1)}x</span>
                    <input
                        type="range"
                        min="0.5" max="2" step="0.1"
                        value={speed}
                        onChange={handleSpeed}
                        style={{ width: '70px', accentColor: 'var(--qd-accent)', cursor: 'pointer' }}
                    />
                </div>

                <div style={{ width: '1px', height: '24px', background: 'var(--qd-border)' }}></div>

                {/* Pitch Lock Toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--qd-space-1)', fontSize: 'var(--qd-text-sm)', color: 'var(--qd-text-muted)', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                        type="checkbox"
                        checked={preservePitch}
                        onChange={togglePitch}
                        style={{ accentColor: 'var(--qd-accent)', cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                    Lock Pitch
                </label>

            </div>
        </div>
    );
};

export default WaveformPlayer;
